import React, { useEffect } from 'react';
import dynamic from 'next/dynamic';
import { NextSeo } from 'next-seo';

import type { LandingPage, StaticContent } from '../../lib/strapi';
import {
  sendEventToAnalytics,
  TagManager,
  TagManagerEvents,
} from '../../lib/analytics';

import type { CookieConsentProps } from '../CookieConsent/CookieConsent';
import { Footer } from '../Footer/Footer';
import { Header } from '../Header/Header';
import { Head } from '../Head/Head';

import { extractSeoProps } from '../../config/seo.config';
import { useCookieConsentValue } from '../CookieConsent';
import { usePageStyleOverrides } from '../../hooks/useStyleOverrides';
import Script from 'next/script';

const CookieConsent = dynamic<CookieConsentProps>(
  () => import('../CookieConsent').then((mod) => mod.CookieConsent),
  { ssr: false },
);

type LayoutProps = {
  content: LandingPage;
  staticContent: StaticContent;
  children?: React.ReactNode;
  currentRoute?: string;
};

export const Layout: React.FC<LayoutProps> = ({
  children,
  content,
  staticContent,
  currentRoute,
}) => {
  const [allowCookies, setAllowCookies] = useCookieConsentValue();

  useEffect(() => {
    const { ConsentGranted, ConsentDenied } = TagManagerEvents;
    if (allowCookies === 'Yes') sendEventToAnalytics(ConsentGranted);
    else sendEventToAnalytics(ConsentDenied);
  }, [allowCookies]);

  useEffect(() => {
    const isCookieModalOpen = allowCookies === 'NotAnswered';
    if (isCookieModalOpen) document.body.style.overflowY = 'hidden';
    else document.body.style.overflowY = 'auto';
  });

  usePageStyleOverrides();

  return (
    <>
      {content.google_tag_manager_id && (
        <>
          <Script
            id="gtm-script"
            strategy="afterInteractive"
            src={`https://www.googletagmanager.com/gtm.js?id=${content.google_tag_manager_id}`}
          />
          <Script id="gtag-init" strategy="afterInteractive">
            {`
            window.dataLayer = window.dataLayer || [];
            function gtag(){window.dataLayer.push(arguments);}
            gtag('consent', 'default', {
              'ad_storage': 'denied',
              'analytics_storage': 'denied',
              'ad_user_data': 'denied',
              'ad_personalization': 'denied'
            });
            gtag('js', new Date());
            gtag('config', '${content.google_tag_manager_id}');
          `}
          </Script>
        </>
      )}
      <NextSeo {...extractSeoProps(content, currentRoute)} />
      <Head content={content} />
      <TagManager id={content.google_tag_manager_id} host={content.domain} />
      <Header content={content} staticContent={staticContent} />
      <main>{children}</main>
      <Footer
        content={content}
        staticContent={staticContent}
        setConsent={setAllowCookies}
      />
      <CookieConsent
        consent={allowCookies}
        setConsent={setAllowCookies}
        staticContent={staticContent.cookie_consent_dialog}
      />
    </>
  );
};
